/* Ardor A-Frame Sign Builder: realistic 3D viewer (three.js r147 classic build).
   Global: window.ArdorAFrame3D.create(hostDiv, opts) -> viewer or null.
   Needs global THREE + THREE.OrbitControls. THREE.RoomEnvironment is optional.
   Units: 1 scene unit = 1 inch. */
(function (root) {
  'use strict';

  var FOV = 28;
  var FIT_MARGIN = 1.1;
  var LEAN = Math.asin(7.5 / 46);          // each panel leans about 9.4 degrees
  var BOARD = 0.16;                         // 4 mm coroplast
  var EXPOSURE = 1.06;                      // ACES exposure for the plastic
  var ENV_I = 0.6;                          // studio environment strength
  var FACE_ENV_I = 0.39;                    // environment strength on the printed faces
  var BACK_I = 0.69;                        // back light strength (the studio environment is darker behind the sign)
  var BOUNCE_I = 0.35;                      // light bounced up off the white floor
  var KEY_I = 0.5;                          // key light strength (env + key is about 1.0 on the sign, so print colours stay true)

  // Frame sizes in inches. T is the thickness of ONE moulded panel.
  var MODELS = {
    deluxe: {
      W: 27, H: 46, T: 1.75, signW: 24, signH: 36, kind: 'loop',
      bodyTop: 42.5, blockTop: 44, hinge: 43.25, holeY: 3.65, clear: 0.2,
      recess: 0.42, bevel: 0.2, tabs: true, bolts: true, barY: 9.2, barInset: 0.75, insert: true
    },
    standard: {
      W: 25, H: 46, T: 1.75, signW: 24, signH: 36, kind: 'loop',
      bodyTop: 42.5, blockTop: 44, hinge: 43.25, holeY: 3.65, clear: 0.1,
      recess: 0.16, bevel: 0.13, tabs: false, bolts: true, barY: 9.2, barInset: 0.3, insert: false
    },
    simpo: {
      W: 24, H: 37, T: 1.15, signW: 22, signH: 28, kind: 'slot',
      bodyTop: 36.25, blockTop: 37, hinge: 36.6, holeY: 3.3, clear: 0.2,
      recess: 0.3, bevel: 0.14, tabs: false, bolts: false, barY: 4.6, barInset: 0.45, insert: true
    }
  };

  function hasThree() {
    var T = root.THREE;
    return !!(T && T.WebGLRenderer && T.OrbitControls && T.ExtrudeGeometry);
  }

  function create(host, opts) {
    try {
      if (!host || !hasThree()) return null;
      return build(host, opts || {});
    } catch (e) {
      return null;
    }
  }

  function build(host, opts) {
    var THREE = root.THREE;
    var doc = host.ownerDocument || document;
    var win = doc.defaultView || root;
    var bg = opts.background || '#ffffff';

    /* ---------- canvas + GL context (probe first so three never logs an error) ---------- */
    var canvas = doc.createElement('canvas');
    canvas.setAttribute('aria-label', '3D preview of your A-frame sign. Drag to turn it.');
    canvas.style.cssText = 'position:absolute;top:0;left:0;right:0;bottom:0;inset:0;width:100%;height:100%;display:block;touch-action:none;outline:none;cursor:grab;';
    var attrs = {
      alpha: false, antialias: true, depth: true, stencil: false, premultipliedAlpha: true,
      preserveDrawingBuffer: false, powerPreference: 'high-performance'
    };
    var gl = null;
    try { gl = canvas.getContext('webgl2', attrs); } catch (e1) { gl = null; }
    if (!gl) { try { gl = canvas.getContext('webgl', attrs) || canvas.getContext('experimental-webgl', attrs); } catch (e2) { gl = null; } }
    if (!gl) return null;

    var renderer = new THREE.WebGLRenderer({
      canvas: canvas, context: gl, antialias: true, alpha: false, stencil: false,
      preserveDrawingBuffer: false, powerPreference: 'high-performance'
    });
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = EXPOSURE;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.VSMShadowMap;
    renderer.shadowMap.autoUpdate = false;      // the shadow only changes when the model is rebuilt
    renderer.setClearColor(new THREE.Color(bg), 1);
    renderer.clippingPlanes = [new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)];   // trims the leaning feet flat on the ground

    try {
      if (win.getComputedStyle && win.getComputedStyle(host).position === 'static') host.style.position = 'relative';
    } catch (e3) { /* ignore */ }
    host.appendChild(canvas);

    var maxAniso = 1;
    try { maxAniso = renderer.capabilities.getMaxAnisotropy() || 1; } catch (e4) { maxAniso = 1; }

    /* ---------- scene, light, ground ---------- */
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(FOV, 1, 2, 3000);
    var disposed = false;
    var envTex = null;

    if (THREE.RoomEnvironment && THREE.PMREMGenerator) {
      try {
        var pmrem = new THREE.PMREMGenerator(renderer);
        var room = new THREE.RoomEnvironment();
        envTex = pmrem.fromScene(room, 0.04).texture;
        scene.environment = envTex;
        pmrem.dispose();
        if (room.dispose) room.dispose();
      } catch (e5) { envTex = null; }
    }
    if (!envTex) {
      scene.add(new THREE.HemisphereLight(0xffffff, 0xd9d9d9, 0.85));
      scene.add(new THREE.AmbientLight(0xffffff, 0.25));
    }

    // bounce from the white floor, so undersides are not left dark
    scene.add(new THREE.HemisphereLight(0x000000, 0xffffff, BOUNCE_I));

    var key = new THREE.DirectionalLight(0xffffff, envTex ? KEY_I : 0.75);
    key.position.set(-22, 130, 40);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.radius = 9;
    key.shadow.blurSamples = 16;
    key.shadow.camera.near = 10;
    key.shadow.camera.far = 320;
    key.shadow.camera.left = -62; key.shadow.camera.right = 62;
    key.shadow.camera.top = 62; key.shadow.camera.bottom = -62;
    key.shadow.bias = -0.0006;
    key.shadow.normalBias = 0.08;
    scene.add(key);
    scene.add(key.target);
    // matching light for the back sign (no shadow)
    var key2 = new THREE.DirectionalLight(0xffffff, envTex ? BACK_I : 0.8);
    key2.position.set(20, 60, -110);
    scene.add(key2);
    scene.add(key2.target);

    var groundMat = new THREE.ShadowMaterial({ color: 0x000000, opacity: 0.15 });
    var groundGeo = new THREE.PlaneGeometry(900, 900);
    var ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0.02;
    ground.receiveShadow = true;
    ground.renderOrder = 1;
    scene.add(ground);

    // baked soft contact shadow
    var blobTex = makeBlobTexture(THREE, doc);
    var blobMat = new THREE.MeshBasicMaterial({
      map: blobTex, transparent: true, opacity: 0.5, depthWrite: false, toneMapped: false, color: 0x000000
    });
    var blobGeo = new THREE.PlaneGeometry(1, 1);
    var blob = new THREE.Mesh(blobGeo, blobMat);
    blob.rotation.x = -Math.PI / 2;
    blob.position.y = 0.04;
    blob.renderOrder = 2;
    scene.add(blob);

    /* ---------- materials (live for the whole viewer) ---------- */
    function lin(hex) { var c = new THREE.Color(hex); c.convertSRGBToLinear(); return c; }

    var grain = makeGrainTexture(THREE, doc);
    var plasticWhite = new THREE.MeshStandardMaterial({
      color: lin('#f4f4f2'), roughness: 0.6, metalness: 0, bumpMap: grain, bumpScale: 0.012, envMapIntensity: ENV_I
    });
    var plasticBlack = new THREE.MeshStandardMaterial({
      color: lin('#1b1b1d'), roughness: 0.52, metalness: 0, bumpMap: grain, bumpScale: 0.012, envMapIntensity: ENV_I * 1.3
    });
    var boltMat = new THREE.MeshStandardMaterial({ color: lin('#a9acb0'), roughness: 0.38, metalness: 0.85 });
    var boardEdgeMat = new THREE.MeshStandardMaterial({ color: lin('#fbfbfa'), roughness: 0.7, metalness: 0, envMapIntensity: FACE_ENV_I, toneMapped: false });
    // unprinted back of a board (a touch darker: it sits in the shade inside the A)
    var boardBackMat = new THREE.MeshStandardMaterial({ color: lin('#dcdcda'), roughness: 0.75, metalness: 0, envMapIntensity: FACE_ENV_I, toneMapped: false });
    // Matte print. Not tone mapped and lit to about 1.0 so the artwork keeps its real colours.
    function printMaterial() {
      var P = THREE.MeshPhysicalMaterial || THREE.MeshStandardMaterial;
      return new P({ color: lin('#ffffff'), roughness: 0.75, metalness: 0, specularIntensity: 0.3, envMapIntensity: FACE_ENV_I, toneMapped: false });
    }
    var faceMats = {
      front: printMaterial(),
      back: printMaterial()
    };
    var faceTex = { front: null, back: null };
    var faceSrc = { front: null, back: null };

    /* ---------- state ---------- */
    var state = { model: 'deluxe', color: 'white', order: 'frame', sides: 2 };
    var modelRoot = null;
    var geos = [];
    var bounds = { hw: 13.5, top: 45.5, hz: 9 };
    var target = new THREE.Vector3(0, 22, 0);
    var fitHome = 100;
    var size = { w: 0, h: 0 };

    var VIEWS = {
      home: { az: 0.56, el: 0.2 },
      front: { az: 0, el: 0.1 },
      back: { az: Math.PI, el: 0.1 }
    };

    /* ---------- controls ---------- */
    var controls = new THREE.OrbitControls(camera, canvas);
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.rotateSpeed = 0.85;
    controls.zoomSpeed = 0.9;
    controls.minPolarAngle = 0.5;
    controls.maxPolarAngle = Math.PI / 2 - 0.02;      // never under the ground
    controls.target.copy(target);

    var raf = 0;
    var tween = null;

    function now() { return (win.performance && win.performance.now) ? win.performance.now() : Date.now(); }

    function invalidate() {
      if (disposed || raf) return;
      raf = win.requestAnimationFrame(frame);
    }

    function frame() {
      raf = 0;
      if (disposed) return;
      if (tween) stepTween();
      controls.update();                 // fires "change" (-> invalidate) while damping is still moving
      renderer.render(scene, camera);
    }

    function renderNow() {
      if (disposed) return;
      renderer.render(scene, camera);
    }

    function onControlsChange() { invalidate(); }
    function onControlsStart() { tween = null; canvas.style.cursor = 'grabbing'; }
    function onControlsEnd() { canvas.style.cursor = 'grab'; }
    controls.addEventListener('change', onControlsChange);
    controls.addEventListener('start', onControlsStart);
    controls.addEventListener('end', onControlsEnd);

    /* ---------- camera helpers ---------- */
    function getSph() {
      var ox = camera.position.x - target.x, oy = camera.position.y - target.y, oz = camera.position.z - target.z;
      var r = Math.sqrt(ox * ox + oy * oy + oz * oz) || 1;
      return { az: Math.atan2(ox, oz), el: Math.asin(Math.max(-1, Math.min(1, oy / r))), r: r };
    }

    function setSph(az, el, r) {
      var ce = Math.cos(el);
      camera.position.set(target.x + r * ce * Math.sin(az), target.y + r * Math.sin(el), target.z + r * ce * Math.cos(az));
      camera.lookAt(target);
    }

    // Camera distance that fits the model's box for a view direction, by the limiting dimension.
    function fitDistance(az, el, aspect) {
      var tv = Math.tan(FOV * Math.PI / 360), th = tv * (aspect || 1);
      var ce = Math.cos(el), se = Math.sin(el), sa = Math.sin(az), ca = Math.cos(az);
      var dx = ce * sa, dy = se, dz = ce * ca;
      var yLo = -0.6 - target.y, yHi = bounds.top - target.y;
      var best = 1, i, x, y, z, cx, cy, cz, d;
      for (i = 0; i < 8; i++) {
        x = (i & 1) ? bounds.hw : -bounds.hw;
        y = (i & 2) ? yHi : yLo;
        z = (i & 4) ? bounds.hz : -bounds.hz;
        cx = x * ca - z * sa;
        cy = -x * se * sa + y * ce - z * se * ca;
        cz = x * dx + y * dy + z * dz;
        d = cz + Math.max(Math.abs(cx) * FIT_MARGIN / th, Math.abs(cy) * FIT_MARGIN / tv);
        if (d > best) best = d;
      }
      return best;
    }

    function aspectNow() { return (size.w > 0 && size.h > 0) ? size.w / size.h : 1.4; }

    function updateLimits() {
      fitHome = fitDistance(VIEWS.home.az, VIEWS.home.el, aspectNow());
      controls.minDistance = fitHome * 0.34;
      controls.maxDistance = fitHome * 1.9;
    }

    function stepTween() {
      var t = (now() - tween.t0) / tween.ms;
      if (t >= 1) t = 1;
      var k = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      setSph(tween.a.az + (tween.b.az - tween.a.az) * k, tween.a.el + (tween.b.el - tween.a.el) * k, tween.a.r + (tween.b.r - tween.a.r) * k);
      if (t >= 1) tween = null; else invalidate();
    }

    function tweenTo(az, el, r, ms) {
      var a = getSph();
      var d = az - a.az;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      tween = { a: a, b: { az: a.az + d, el: el, r: r }, t0: now(), ms: ms || 520 };
      invalidate();
    }

    /* ---------- geometry helpers ---------- */
    function track(g) { geos.push(g); return g; }

    // Closed outline with rounded corners. pts = [[x, y, cut], ...] (cut = how far the corner is trimmed back along each edge).
    function roundedPath(path, pts) {
      var n = pts.length, i, p, a, b, l1, l2, d, ax, ay, bx, by;
      for (i = 0; i < n; i++) {
        p = pts[i]; a = pts[(i - 1 + n) % n]; b = pts[(i + 1) % n];
        l1 = Math.sqrt((a[0] - p[0]) * (a[0] - p[0]) + (a[1] - p[1]) * (a[1] - p[1])) || 1;
        l2 = Math.sqrt((b[0] - p[0]) * (b[0] - p[0]) + (b[1] - p[1]) * (b[1] - p[1])) || 1;
        d = Math.min(p[2] || 0, l1 * 0.49, l2 * 0.49);
        ax = p[0] + (a[0] - p[0]) / l1 * d; ay = p[1] + (a[1] - p[1]) / l1 * d;
        bx = p[0] + (b[0] - p[0]) / l2 * d; by = p[1] + (b[1] - p[1]) / l2 * d;
        if (i === 0) path.moveTo(ax, ay); else path.lineTo(ax, ay);
        if (d > 0.001) path.quadraticCurveTo(p[0], p[1], bx, by);
      }
      path.closePath();
      return path;
    }

    function rectPath(path, x0, y0, x1, y1, r) {
      return roundedPath(path, [[x0, y0, r], [x1, y0, r], [x1, y1, r], [x0, y1, r]]);
    }

    function solid(geo, mat, cast) {
      var m = new THREE.Mesh(track(geo), mat);
      m.castShadow = cast !== false;
      m.receiveShadow = true;
      return m;
    }

    // Extrude a shape from z0 to z1 with a moulded (bevelled) edge that stays inside the outline.
    function extrude(shape, z0, z1, bevel, mat) {
      var b = Math.max(0, Math.min(bevel || 0, (z1 - z0) * 0.45));
      var geo = new THREE.ExtrudeGeometry(shape, {
        depth: (z1 - z0) - 2 * b, steps: 1, curveSegments: 8,
        bevelEnabled: b > 0, bevelThickness: b, bevelSize: b, bevelOffset: -b, bevelSegments: 3
      });
      geo.translate(0, 0, z0 + b);
      return solid(geo, mat);
    }

    function box(w, h, d, x, y, z, mat, cast) {
      var m = solid(new THREE.BoxGeometry(w, h, d), mat, cast);
      m.position.set(x, y, z);
      return m;
    }

    function signBoard(w, h, face) {
      // +z carries the print, the other faces are white coroplast
      var mats = [boardEdgeMat, boardEdgeMat, boardEdgeMat, boardEdgeMat, face ? faceMats[face] : boardEdgeMat, boardBackMat];
      return solid(new THREE.BoxGeometry(w, h, BOARD), mats);
    }

    function loopOutline(M) {
      var w = M.W / 2, H = M.H, bt = M.bodyTop, kt = M.blockTop;
      return [
        [-w, 0, 0.6], [-w + 5.2, 0, 0.5], [-w + 6.7, 1.5, 1.2], [0, 2.25, 14], [w - 6.7, 1.5, 1.2], [w - 5.2, 0, 0.5], [w, 0, 0.6],
        [w, kt, 0.55], [w - 3.5, kt, 0.55], [w - 3.5, bt, 0.4],
        [3.75, bt, 0.55], [2.7, H, 1.3], [-2.7, H, 1.3], [-3.75, bt, 0.55],
        [-w + 3.5, bt, 0.4], [-w + 3.5, kt, 0.55], [-w, kt, 0.55]
      ];
    }

    function slotOutline(M) {
      var w = M.W / 2, bt = M.bodyTop, kt = M.blockTop;
      return [
        [-w, 0, 1.7], [-w + 4.6, 0, 0.45], [-w + 5.8, 0.9, 1.0], [0, 1.35, 12], [w - 5.8, 0.9, 1.0], [w - 4.6, 0, 0.45], [w, 0, 1.7],
        [w, kt, 0.6], [w - 3.3, kt, 0.4], [w - 3.75, bt, 0.3],
        [-w + 3.75, bt, 0.3], [-w + 3.3, kt, 0.4], [-w, kt, 0.6]
      ];
    }

    // One moulded panel. Local space: x centred, y 0 (feet) .. H, z 0 (inside of the A) .. T (sign side).
    function buildPanel(M, plastic, face) {
      var g = new THREE.Group();
      var T = M.T, w = M.W / 2;
      var hx = M.signW / 2 + M.clear / 2;                 // recess half width
      var hy0 = M.holeY, hy1 = M.holeY + M.signH + M.clear;
      var hcy = (hy0 + hy1) / 2;
      var floorZ = T - M.recess;
      var shape = new THREE.Shape();
      var hole, ring, win2, i, sx, b;

      if (M.kind === 'loop') {
        roundedPath(shape, loopOutline(M));
        hole = new THREE.Path();                          // hand hole in the carry loop
        rectPath(hole, -1.85, M.bodyTop + 1.1, 1.85, M.bodyTop + 2.4, 0.62);
        shape.holes.push(hole);
      } else {
        roundedPath(shape, slotOutline(M));
        hole = new THREE.Path();                          // oblong hand slot in the header
        rectPath(hole, -2.9, hy1 + 1.75, 2.9, hy1 + 3.2, 0.72);
        shape.holes.push(hole);
      }
      hole = new THREE.Path();                            // sign recess
      rectPath(hole, -hx, hy0, hx, hy1, 0.28);
      shape.holes.push(hole);
      g.add(extrude(shape, 0, T, M.bevel, plastic));

      if (M.kind === 'loop') {
        // solid recessed face
        g.add(box(hx * 2 + 0.5, (hy1 - hy0) + 0.5, floorZ - 0.06, 0, hcy, (floorZ + 0.06) / 2, plastic));
      } else {
        // open picture frame: a ledge around a see-through window
        ring = new THREE.Shape();
        rectPath(ring, -hx - 0.25, hy0 - 0.25, hx + 0.25, hy1 + 0.25, 0.3);
        win2 = new THREE.Path();
        rectPath(win2, -hx + 0.85, hy0 + 0.85, hx - 0.85, hy1 - 0.85, 1.5);
        ring.holes.push(win2);
        g.add(extrude(ring, 0.05, floorZ, 0.07, plastic));
      }

      if (M.tabs) {
        for (i = 0; i < 2; i++) {
          sx = i ? 1 : -1;
          g.add(box(1.25, 1.7, 0.3, sx * (hx - 0.3), hcy, T - 0.13, plastic));
        }
      }

      if (M.bolts) {
        for (i = 0; i < 2; i++) {
          sx = i ? 1 : -1;
          b = solid(new THREE.SphereGeometry(0.46, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2), boltMat);
          b.rotation.x = Math.PI / 2;
          b.scale.set(1, 0.42, 1);
          b.position.set(sx * (w - 1.75), (hy1 + M.bodyTop) / 2, T - 0.02);
          g.add(b);
        }
      }

      if (face) {
        if (M.insert) {
          b = signBoard(M.signW, M.signH, face);
          b.position.set(0, hcy, floorZ + BOARD / 2 + 0.005);
          g.add(b);
        } else {
          // printed vinyl stuck straight onto the recessed face
          b = solid(new THREE.PlaneGeometry(M.signW, M.signH), faceMats[face], false);
          b.position.set(0, hcy, floorZ + 0.014);
          g.add(b);
        }
      }
      return g;
    }

    function hingeAt(obj, hingeY, pivotY, lean, flip) {
      var inner = new THREE.Group();
      obj.position.y = -hingeY;
      inner.add(obj);
      inner.position.y = pivotY;
      inner.rotation.x = -lean;
      var outer = new THREE.Group();
      if (flip) outer.rotation.y = Math.PI;
      outer.add(inner);
      return outer;
    }

    function clearModel() {
      var i;
      if (modelRoot) scene.remove(modelRoot);
      for (i = 0; i < geos.length; i++) geos[i].dispose();
      geos = [];
      modelRoot = null;
    }

    function rebuild() {
      clearModel();
      var M = MODELS[state.model] || MODELS.deluxe;
      var inserts = state.order === 'inserts' && M.insert;
      var two = state.sides !== 1;
      var plastic = (state.color === 'black' && state.model === 'deluxe') ? plasticBlack : plasticWhite;
      var grp = new THREE.Group();
      var pivotY, i, L, bar, b, lean;

      if (!inserts) {
        pivotY = M.hinge * Math.cos(LEAN) - M.T * Math.sin(LEAN) + 0.02;
        grp.add(hingeAt(buildPanel(M, plastic, 'front'), M.hinge, pivotY, LEAN, false));
        grp.add(hingeAt(buildPanel(M, plastic, two ? 'back' : null), M.hinge, pivotY, LEAN, true));
        // stabiliser bars tying the two panels together
        L = 2 * (pivotY - M.barY) * Math.tan(LEAN) + 0.5;
        for (i = 0; i < 2; i++) {
          bar = box(0.16, 1.0, L, (i ? 1 : -1) * (M.W / 2 - M.barInset), M.barY, 0, plastic);
          grp.add(bar);
        }
        bounds = {
          hw: M.W / 2,
          top: pivotY + (M.H - M.hinge) * Math.cos(LEAN) + 0.2,
          hz: M.hinge * Math.sin(LEAN) + M.T + 0.2
        };
      } else if (two) {
        // two loose boards leaning on each other like a tent
        lean = 0.2;
        pivotY = M.signH * Math.cos(lean) + 0.02;
        b = signBoard(M.signW, M.signH, 'front');
        b.position.set(0, M.signH / 2, BOARD / 2);
        grp.add(hingeAt(wrap(b), M.signH, pivotY, lean, false));
        b = signBoard(M.signW, M.signH, 'back');
        b.position.set(0, M.signH / 2, BOARD / 2);
        grp.add(hingeAt(wrap(b), M.signH, pivotY, lean, true));
        bounds = { hw: M.signW / 2, top: pivotY + 0.3, hz: M.signH * Math.sin(lean) + 0.5 };
      } else {
        // one loose board, leaning back a little
        lean = 0.14;
        pivotY = M.signH * Math.cos(lean) + 0.02;
        b = signBoard(M.signW, M.signH, 'front');
        b.position.set(0, M.signH / 2, -BOARD / 2);
        var one = hingeAt(wrap(b), M.signH, pivotY, lean, false);
        one.position.z = -M.signH * Math.sin(lean) / 2;
        grp.add(one);
        bounds = { hw: M.signW / 2, top: pivotY + 0.3, hz: M.signH * Math.sin(lean) / 2 + 0.5 };
      }

      modelRoot = grp;
      scene.add(grp);

      target.set(0, bounds.top * 0.5 - 0.4, 0);
      blob.scale.set(bounds.hw * 2 + 22, bounds.hz * 2 + 20, 1);
      blobMat.opacity = inserts ? 0.3 : 0.5;
      key.target.position.set(0, bounds.top * 0.4, 0);
      key.target.updateMatrixWorld();
      key2.target.position.copy(key.target.position);
      key2.target.updateMatrixWorld();
      renderer.shadowMap.needsUpdate = true;
    }

    function wrap(obj) { var g = new THREE.Group(); g.add(obj); return g; }

    /* ---------- textures ---------- */
    function applyFace(face) {
      var src = faceSrc[face];
      var mat = faceMats[face];
      if (faceTex[face]) { faceTex[face].dispose(); faceTex[face] = null; }
      if (src && src.width > 0 && src.height > 0) {
        var t = new THREE.CanvasTexture(src);
        t.encoding = THREE.sRGBEncoding;
        t.anisotropy = maxAniso;
        t.generateMipmaps = true;
        t.minFilter = THREE.LinearMipmapLinearFilter;
        t.magFilter = THREE.LinearFilter;
        t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
        t.needsUpdate = true;
        faceTex[face] = t;
        mat.map = t;
      } else {
        mat.map = null;
      }
      mat.needsUpdate = true;
    }

    /* ---------- sizing ---------- */
    function measure() {
      var w = host.clientWidth | 0, h = host.clientHeight | 0;
      if (w < 2 || h < 2) return false;
      var pr = Math.min(2, Math.max(1, win.devicePixelRatio || 1));
      if (w === size.w && h === size.h && pr === renderer.getPixelRatio()) return false;
      var oldFit = fitHome, had = size.w > 0;
      size.w = w; size.h = h;
      renderer.setPixelRatio(pr);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      updateLimits();
      var s = getSph();
      if (had && !tween) setSph(s.az, s.el, Math.max(controls.minDistance, Math.min(controls.maxDistance, s.r * fitHome / oldFit)));
      else if (!had) setSph(VIEWS.home.az, VIEWS.home.el, fitHome);
      else if (tween) tween.b.r = tween.b.r * fitHome / oldFit;
      controls.update();
      return true;
    }

    /* ---------- public API ---------- */
    var api = {
      set: function (o) {
        if (disposed || !o) return;
        var changed = false, k, v;
        for (k in state) {
          if (!Object.prototype.hasOwnProperty.call(state, k) || o[k] === undefined || o[k] === null) continue;
          v = o[k];
          if (k === 'sides') v = (Number(v) === 1) ? 1 : 2;
          if (k === 'model' && !MODELS[v]) continue;
          if (state[k] !== v) { state[k] = v; changed = true; }
        }
        if (!changed && modelRoot) return;
        var oldFit = fitHome, s = getSph();
        rebuild();
        controls.target.copy(target);
        updateLimits();
        if (tween) tween.b.r = tween.b.r * fitHome / oldFit;
        setSph(s.az, s.el, Math.max(controls.minDistance, Math.min(controls.maxDistance, s.r * fitHome / oldFit)));
        controls.update();
        invalidate();
      },
      setFace: function (face, cv) {
        if (disposed || (face !== 'front' && face !== 'back')) return;
        faceSrc[face] = cv || null;
        applyFace(face);
        invalidate();
      },
      refresh: function () {
        if (disposed) return;
        if (faceTex.front) faceTex.front.needsUpdate = true;
        if (faceTex.back) faceTex.back.needsUpdate = true;
        invalidate();
      },
      view: function (name) {
        if (disposed) return;
        var v = VIEWS[name] || VIEWS.home;
        tweenTo(v.az, v.el, fitDistance(v.az, v.el, aspectNow()), 560);
      },
      zoom: function (dir) {
        if (disposed) return;
        var s = tween ? tween.b : getSph();
        var r = s.r * Math.pow(0.8, dir > 0 ? 1 : -1);
        r = Math.max(controls.minDistance, Math.min(controls.maxDistance, r));
        tweenTo(s.az, s.el, r, 240);
      },
      resize: function () {
        if (disposed) return;
        if (measure()) { raf && win.cancelAnimationFrame(raf); raf = 0; renderNow(); if (tween) invalidate(); }
      },
      snapshot: function (w, h, viewName) {
        if (disposed) return null;
        var out = null;
        var oldPR = renderer.getPixelRatio();
        var oldSize = renderer.getSize(new THREE.Vector2());
        var oldAspect = camera.aspect;
        var oldPos = camera.position.clone();
        try {
          var cap = Math.min(4096, renderer.capabilities.maxTextureSize || 4096);
          w = Math.max(16, Math.min(cap, Math.round(w || size.w || 800)));
          h = Math.max(16, Math.min(cap, Math.round(h || size.h || 600)));
          var s = getSph();
          var k = fitDistance(s.az, s.el, w / h) / fitDistance(s.az, s.el, oldAspect || 1);
          renderer.setPixelRatio(1);
          renderer.setSize(w, h, false);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          var sv = (typeof viewName === 'string' && VIEWS.hasOwnProperty(viewName)) ? VIEWS[viewName] : null; // proof sheet (ardor-proofsheet.js): a named view, fitted to the picture
if (sv) setSph(sv.az, sv.el, fitDistance(sv.az, sv.el, w / h)); else setSph(s.az, s.el, s.r * k);
          renderer.render(scene, camera);
          out = doc.createElement('canvas');
          out.width = w; out.height = h;
          var ctx = out.getContext('2d');
          ctx.fillStyle = bg;
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(canvas, 0, 0, w, h);
        } catch (e) {
          out = null;
        }
        try {
          renderer.setPixelRatio(oldPR);
          renderer.setSize(oldSize.x, oldSize.y, false);
          camera.aspect = oldAspect;
          camera.updateProjectionMatrix();
          camera.position.copy(oldPos);
          camera.lookAt(target);
          renderer.render(scene, camera);
        } catch (e2) { /* ignore */ }
        return out;
      },
      dispose: function () {
        if (disposed) return;
        disposed = true;
        if (raf) win.cancelAnimationFrame(raf);
        raf = 0; tween = null;
        try { if (ro) ro.disconnect(); } catch (e) { /* ignore */ }
        win.removeEventListener('resize', onWinResize);
        canvas.removeEventListener('webglcontextlost', onLost, false);
        canvas.removeEventListener('webglcontextrestored', onRestored, false);
        controls.removeEventListener('change', onControlsChange);
        controls.removeEventListener('start', onControlsStart);
        controls.removeEventListener('end', onControlsEnd);
        controls.dispose();
        clearModel();
        var list = [plasticWhite, plasticBlack, boltMat, boardEdgeMat, boardBackMat, faceMats.front, faceMats.back, groundMat, blobMat,
          grain, blobTex, groundGeo, blobGeo, faceTex.front, faceTex.back, envTex];
        for (var i = 0; i < list.length; i++) { try { if (list[i]) list[i].dispose(); } catch (e3) { /* ignore */ } }
        try { renderer.dispose(); renderer.forceContextLoss(); } catch (e4) { /* ignore */ }
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      }
    };

    function onWinResize() { api.resize(); }
    function onLost(e) { e.preventDefault(); }
    function onRestored() { renderer.shadowMap.needsUpdate = true; invalidate(); }
    canvas.addEventListener('webglcontextlost', onLost, false);
    canvas.addEventListener('webglcontextrestored', onRestored, false);

    var ro = null;
    if (win.ResizeObserver) {
      ro = new win.ResizeObserver(function () { api.resize(); });
      ro.observe(host);
    }
    win.addEventListener('resize', onWinResize);

    rebuild();
    controls.target.copy(target);
    updateLimits();
    setSph(VIEWS.home.az, VIEWS.home.el, fitHome);
    if (!measure()) controls.update();
    invalidate();

    return api;
  }

  function makeGrainTexture(THREE, doc) {
    var n = 128, c = doc.createElement('canvas');
    c.width = c.height = n;
    var g = c.getContext('2d'), img = g.createImageData(n, n), seed = 20241, i, v;
    for (i = 0; i < n * n; i++) {
      seed = (seed * 16807) % 2147483647;
      v = 112 + (seed / 2147483647) * 32;
      img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    var t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(0.45, 0.45);
    return t;
  }

  function makeBlobTexture(THREE, doc) {
    var n = 256, c = doc.createElement('canvas');
    c.width = c.height = n;
    var g = c.getContext('2d');
    var gr = g.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
    gr.addColorStop(0, 'rgba(255,255,255,0.62)');
    gr.addColorStop(0.35, 'rgba(255,255,255,0.4)');
    gr.addColorStop(0.7, 'rgba(255,255,255,0.1)');
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr;
    g.fillRect(0, 0, n, n);
    return new THREE.CanvasTexture(c);
  }

  root.ArdorAFrame3D = { create: create };
})(typeof window !== 'undefined' ? window : this);
